import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';

/**
 * The same-origin script the transcoder workers start from (`apps/web/public/decoders/`), under
 * the `/decoders/` prefix the dev, preview and edge servers exempt from the page's policy.
 */
export const KTX2_WORKER_URL = `${import.meta.env.BASE_URL}decoders/ktx2-worker.js`;

/** The first message the shell waits for: the worker script three composed, as a blob URL. */
export interface Ktx2WorkerLoad {
  type: 'steelwake:load';
  url: string;
}

/**
 * three's KTX2Loader with its transcoder workers started from a same-origin script instead of
 * the blob URL it composes. A blob worker inherits the page's Content-Security-Policy, and the
 * Basis transcoder's embind glue builds its invokers with `new Function`, which the strict page
 * policy (no `'unsafe-eval'`) refuses: every texture load then hangs. A worker fetched from a
 * URL takes its policy from its own response, so the shell is served without the page's header
 * and `importScripts` the blob three built; every message after that reaches three's own worker
 * code unchanged (the pool, the transcoder binary and the config are three's).
 */
export class Ktx2Loader extends KTX2Loader {
  override init(): Promise<void> {
    return super.init().then(() => {
      this.workerPool.setWorkerCreator(() => {
        const binary = this.transcoderBinary;
        if (binary === null) throw new Error('KTX2Loader: no transcoder binary after init');
        const worker = new Worker(KTX2_WORKER_URL);
        const load: Ktx2WorkerLoad = { type: 'steelwake:load', url: this.workerSourceURL };
        worker.postMessage(load);
        const transcoderBinary = binary.slice(0);
        worker.postMessage({ type: 'init', config: this.workerConfig, transcoderBinary }, [
          transcoderBinary,
        ]);
        return worker;
      });
    });
  }
}
